import React from 'react';
import Styles from './index.less';
import {
    IInitPreviewLayerProps,
    IRenderContext,
    ITransitionService,
    TransitionAutoPreview,
} from '@src/slide/application/service/transition-service/transition-service-interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { DeepPartial } from '@src/slide/infrastructure/module/independent/ts-tools/deep-partial';
import { TransitionType } from '@src/slide/domain/ui-model/element-modeles/transition/constants';
import { TransitionPresets } from '@src/slide/application/service/transition-service/preset';
import { transaction } from '@src/slide/domain/slide-app';
import { ITransition } from '@src/slide/domain/ui-model/element-modeles/transition/interface';
import { RenderLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/thumbnail';
import { RenderPostionType } from '@src/js/constant';
import { TransitionPreview } from '@src/slide/application/service/transition-service/preview';
import ReactDOM from 'react-dom';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import {
    IEditorViewableServiceLayer,
} from '@src/slide/application/entry/toc/editor/base/service-layer/viewable-service-layer.interface';
import {
    IEditorEditableServiceLayer,
} from '@src/slide/application/entry/toc/editor/base/service-layer/editable-service-layer.interface';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import { observable } from 'mobx';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

export default class TransitionService implements ITransitionService {
    @observable public autoPreviewState: boolean;

    private transitionPreviewLayer: HTMLDivElement;
    private transitionPreviewContainer?: HTMLDivElement;
    private layerRenderQueue: Promise<undefined>[] = [];

    private renderContext?: IRenderContext;
    private enterCanvas?: HTMLCanvasElement;
    private exitCanvas?: HTMLCanvasElement;
    private enterSlideRenderLayer?: RenderLayerService;
    private exitSlideRenderLayer?: RenderLayerService;

    private isPreviewing?: boolean;

    public constructor(
        private uiDataService: IUiDataService,
        private selectionService: ISelectionService,
        private mediaManager: IMediaManager,
        private editableServiceLayer: IEditorEditableServiceLayer,
        private viewableServiceLayer: IEditorViewableServiceLayer,
        private infrastructure: IInfrastructureBase,
    ) {
        const autoPreviewState = userLocalStorage.getItem(
            LocalStorageKeys.TRANSITION_AUTO_PREVIEW,
            TransitionAutoPreview.ENABLE,
        );
        this.autoPreviewState = autoPreviewState === TransitionAutoPreview.ENABLE;
    }

    public initPreviewLayer(props: IInitPreviewLayerProps) {
        this.initLayerStyle(props);
        this.updateLayerStyle(props);
    }

    public updatePreviewLayer(props: Omit<IInitPreviewLayerProps, 'ref'>) {
        if (!this.transitionPreviewLayer) return;

        this.stopPreview();

        this.updateLayerStyle(props);
        this.updateRenderContext(props);
    }

    public getCurrentSlideTransition() {
        return this.currentSlide?.transition;
    }

    public updateTransitionType(type: TransitionType) {
        const currentTransitionType = this.currentSlide?.transition?.type;
        if (type === currentTransitionType) return;

        transaction(() => {
            if (type === TransitionType.EMPTY) {
                this.updateTransition(null);
                // 在没有transition字段的时候，会走transition继承的逻辑
                // 所以在设置为无切换时，需要删除transition的内容之后，增加一个空的transition字段来防止继承逻辑
                this.updateTransition({});
                return;
            }

            if (currentTransitionType) {
                // 清除之前的transition，切换类型直接替换为对应类型的预设数据
                this.updateTransition(null);
            }

            const transitionProps: DeepPartial<ITransition> = { type, ...TransitionPresets[type] };
            this.updateTransition(transitionProps);
        });
    }

    public updateTransitionEffect(effect: Partial<ITransition>) {
        const type = this.currentSlide?.transition?.type;
        if (!type) return;

        transaction(() => {
            const transitionProps: DeepPartial<ITransition> = { type, ...effect };
            this.updateTransition(transitionProps);
        });
    }

    public updateTransition(transitionProps: DeepPartial<ITransition> | null) {
        this.getTransitionProperty(this.currentSlide)?.updateProps(transitionProps);
    }

    // 应用到全部，按Office的逻辑，会应用到全部slide、layout和Master
    public applyTransitionToAll() {
        const transition = this.getCurrentSlideTransition();
        transaction(() => {
            this.getAllModelList().forEach((model) => {
                this.getTransitionProperty(model)?.removeTransition();
                this.getTransitionProperty(model)?.updateProps(transition as ITransition);
            });
        });
    }

    public get currentSlide() {
        return this.selectionService.currentSlide;
    }

    public getPreviewState() {
        return !!this.isPreviewing;
    }

    public preview() {
        if (!this.currentSlide) return;

        // 先停止当前的preview和清空之前的layer
        this.stopPreview();

        // 空数据或者无切换 不进行预览
        if (!this.currentSlide.transition?.type || this.currentSlide.transition?.type === TransitionType.EMPTY) {
            return;
        }

        this.setPreviewingState(true);

        this.enterCanvas = this.createCanvasElement();
        this.enterSlideRenderLayer = this.createSlideRenderLayer(this.enterCanvas, this.currentSlide);

        if (this.exitSlideModel) {
            this.exitCanvas = this.createCanvasElement();
            this.exitSlideRenderLayer = this.createSlideRenderLayer(this.exitCanvas, this.exitSlideModel);
        }

        Promise.all(this.layerRenderQueue).then(() => {
            if (!this.isPreviewing) return;

            this.mountPreviewContainer();
        });
    }

    public stopPreview() {
        this.onTransitionStop();
    }

    public toggleAutoPreview() {
        this.autoPreviewState = !this.autoPreviewState;
        userLocalStorage.setItem(
            LocalStorageKeys.TRANSITION_AUTO_PREVIEW,
            this.autoPreviewState ? TransitionAutoPreview.ENABLE : TransitionAutoPreview.DISABLE,
        );
    }

    private createCanvasElement() {
        return document.createElement('canvas');
    }

    private createSlideRenderLayer(canvas: HTMLCanvasElement, slideModel: ISlideModel): RenderLayerService {
        let resolver: (value: any) => void;
        const renderPromise = new Promise<undefined>(resolve => resolver = resolve);
        this.layerRenderQueue.push(renderPromise);

        return new RenderLayerService(
            slideModel,
            canvas,
            this.renderContext?.scale,
            null,
            RenderPostionType.TRANSITION_PREVIEW,
            false,
            false,
            this.infrastructure,
            this.uiDataService,
            this.mediaManager,
            () => {
                resolver(undefined);
            },
            this.selectionService,
            this.viewableServiceLayer,
            this.editableServiceLayer,
        );
    }

    private mountPreviewContainer() {
        const container = this.createTransitionPreviewContainer();
        ReactDOM.render(<TransitionPreview
            contentSize={this.renderContentSize}
            enterSlideModel={this.currentSlide!}
            exitSlideModel={this.exitSlideModel}
            enterCanvas={this.enterCanvas!}
            exitCanvas={this.exitCanvas}
            enterBrowserCanvas={this.enterSlideRenderLayer?.getApplication().view}
            exitBrowserCanvas={this.exitSlideRenderLayer?.getApplication().view}
            onTransitionStart={this.onTransitionStart}
            onTransitionStop={this.onTransitionStop}
        />, container);

        this.transitionPreviewContainer = container;
        this.transitionPreviewLayer.appendChild(container);
    }

    private createTransitionPreviewContainer() {
        const container = document.createElement('div');
        const { height, width, top, left } = this.renderContentSize;
        const { currentScrollY = 0, currentScrollX = 0 } = this.renderContext || {};
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        // renderContext中的top和left是画布实际的位置。而wrapper需要在滚动后平移到当前的视口处、
        // container相对wrapper定位，所以需要根据滚动位置计算差值
        container.style.top = `${top + currentScrollY}px`;
        container.style.left = `${left + currentScrollX}px`;
        container.style.background = '#000';
        container.style.overflow = 'hidden';
        container.className = Styles['transition-preview'];

        return container;
    }

    private onTransitionStart = () => {
        this.setTransitionPreviewLayerVisible(true);
    };

    private onTransitionStop = () => {
        this.resetPreview();
        this.resetRenderLayer();
    };

    private get renderContentSize() {
        const { height = 0, width = 0 } = this.enterSlideRenderLayer?.getApplication() || {};
        const { left = 0, top = 0 } = this.renderContext?.contentSize || {};

        return { width, height, left, top  };
    }

    private getAllModelList() {
        const { slideModelList, layoutModelList, masterModelList } = this.uiDataService;

        return [...masterModelList, ...layoutModelList, ...slideModelList];
    }

    private initLayerStyle(props: IInitPreviewLayerProps) {
        const { width, height, ref } = props;
        this.transitionPreviewLayer = ref;
        this.transitionPreviewLayer.style.width = `${width}px`;
        this.transitionPreviewLayer.style.height = `${height}px`;
        this.setTransitionPreviewLayerVisible(false);
    }

    private updateLayerStyle(props: Omit<IInitPreviewLayerProps, 'ref'>) {
        const { width, height, renderRectConfig } = props;
        const { currentScrollX = 0, currentScrollY = 0 } = renderRectConfig;

        this.transitionPreviewLayer.style.width = `${width}px`;
        this.transitionPreviewLayer.style.height = `${height}px`;
        this.transitionPreviewLayer.style.transform = `translate(${-currentScrollX}px, ${-currentScrollY}px)`;
    }

    private updateRenderContext(props: Omit<IInitPreviewLayerProps, 'ref'>) {
        const { width, height, scale, renderRectConfig } = props;
        this.renderContext = {
            width,
            height,
            scale,
            ...renderRectConfig,
        };
    }

    private resetPreview() {
        this.setPreviewingState(false);
        this.setTransitionPreviewLayerVisible(false);
    }

    private setTransitionPreviewLayerVisible(visible = true) {
        this.transitionPreviewLayer.style.display = visible ? 'block' : 'none';
    }

    private resetRenderLayer() {
        this.enterCanvas = undefined;
        this.exitCanvas = undefined;
        this.enterSlideRenderLayer = undefined;
        this.exitSlideRenderLayer = undefined;
        this.layerRenderQueue = [];

        this.disposeWrapper();
    }

    private disposeWrapper() {
        if (this.transitionPreviewContainer) {
            // 直接移除dom不会触发react的生命周期hook，这里通过ReactDom来触发组件的unMount
            ReactDOM.unmountComponentAtNode(this.transitionPreviewContainer);
            this.transitionPreviewContainer = undefined;
        }
        this.transitionPreviewLayer.innerHTML = '';
    }

    private get exitSlideModel(): ISlideModel | undefined {
        const currentIndex = this.currentSlide?.presentationRef?.indexOfSlide(this.currentSlide);
        if (currentIndex && currentIndex > 0) {
            const isMasterEditType = this.currentSlide?.getMasterLayoutEditService()?.isMasterEditType;
            // slide编辑
            if (!isMasterEditType) {
                return this.uiDataService.slideModelList[currentIndex - 1];
            }

            // 母版layout编辑
            if (this.currentSlide?.isLayout) {
                return this.uiDataService.layoutModelList[currentIndex - 1];
            }

            // 母版master编辑
            if (this.currentSlide?.isMaster) {
                return this.uiDataService.masterModelList[currentIndex - 1];
            }
        }
    }

    private setPreviewingState(isPreviewing: boolean) {
        this.isPreviewing = isPreviewing;
    }

    private getTransitionProperty(model?: ISlideModel) {
        return model?.getTransitionProperty();
    }
}
